import React from "react";
import {
  Table as Table_,
  TableBody,
  TableCell as TableCell_,
  TableFooter,
  TableHead,
  TableHeader,
  TableProps,
  TableRow as TableRow__,
} from "@/components/ui/table";
import { Cell, ColumnDef, flexRender, Row, Table } from "@tanstack/react-table";
import { DataTableColumnHeader } from "./data-table-column-header";
import { cn } from "@/lib/utils";
import { Loader } from "../loader";
import { ScrollAreaProps } from "@/components/ui/scroll-area";
import {
  AccordionGroupingState,
  AccordionTableRows,
  groupRowsByColumn,
} from "./data-table-accordion";
import { TPinnedRowMeta } from "./data-table-context";
import { useResizeObserver } from "@/hooks/use-resize-observer";
import { useNoResultsMessage } from "./data-table-no-results";
import { getColumnDefId } from "./table-settings/table-settings-layout";

export interface DataTableColumnsProps<TData, TValue> extends TableProps {
  table: Table<TData>;
  columns: ColumnDef<TData, TValue>[];
  fill?: boolean;
  loading?: boolean;
  scroll?: ScrollAreaProps;
  currentRef?: React.MutableRefObject<HTMLDivElement | null>;
  noResultsMessage?: string;
  accordionGrouping?: AccordionGroupingState;
  onToggleAccordionGroup?: (groupKey: string) => void;
  stickyHeader?: boolean;
  stickyFooter?: boolean;
  footerAsSecondaryHeader?: boolean;
}

const parsePixelWidth = (value: unknown): number | undefined => {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return undefined;
  const match = value.trim().match(/^(-?\d+(?:\.\d+)?)px$/i);
  return match ? Number(match[1]) : undefined;
};

const renderedColumnWidth = (value: unknown, fallback: string) =>
  parsePixelWidth(value) === undefined ? fallback : (value as number | string);

export const TableCell = <TData, TValue>({
  table,
  row,
  cell,
}: {
  table: Table<TData>;
  row: Row<TData>;
  cell: Cell<TData, TValue>;
}) => {
  const chain =
    <T,>(cb?: (ev: T) => any) =>
    (ev: T) => {
      if (
        cell.column.isPreviewEnabled() &&
        (!row.getIsPosition() || (table.getState().preview ?? 0) === 0)
      ) {
        row.setIsPosition(true);
        table.setPreview((u) =>
          u === 0 ? (table.isSkipPreview() ? 2 : 1) : u,
        );
        return;
      }
      if (cb) {
        cb(ev);
      }
    };
  const meta = (cell?.column?.columnDef?.meta as any) ?? {};
  return (
    <TableCell_
      {...meta?.props}
      key={cell.id}
      className={cn(cell.column.isPreviewEnabled() && "cursor-pointer")}
      style={{
        ...meta?.props?.style,
        width: renderedColumnWidth(
          meta?.width,
          `calc(var(--col-${cell.column.id}-size))`,
        ),
        minWidth: meta?.minWidth,
        maxWidth: meta?.maxWidth,
      }}
      onClick={chain(meta?.props?.onClick)}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </TableCell_>
  );
};

/**
 * A row under `row`, one cell per visible column, sized exactly like the table's own cells -- so it lines
 * up with the columns, follows column resizing, and its background spans the full width. What each cell
 * shows comes from `renderCell`. Its clicks don't reach the row above.
 */
export const DataTableDetailRow = <TData,>({
  row,
  renderCell,
  className,
  onClick,
  ...props
}: {
  row: Row<TData>;
  renderCell: (columnId: string) => React.ReactNode;
} & React.HTMLAttributes<HTMLTableRowElement>) => (
  <tr
    data-component="data-table-detail-row"
    className={cn("border-b transition-colors", className)}
    onClick={onClick}
    {...props}
  >
    {row.getVisibleCells().map((cell) => {
      const meta = (cell.column.columnDef.meta as any) ?? {};
      return (
        <TableCell_
          key={cell.id}
          style={{
            width: renderedColumnWidth(
              meta?.width,
              `calc(var(--col-${cell.column.id}-size))`,
            ),
            minWidth: meta?.minWidth,
            maxWidth: meta?.maxWidth,
          }}
        >
          {renderCell(cell.column.id)}
        </TableCell_>
      );
    })}
  </tr>
);

const TableRow_ = <TData,>({
  className,
  table,
  row,
  onClick,
  isSelected,
  isPosition,
  isPinned,
  allSelected: _allSelected,
  currentRef,
  cells: _cells,
  ...props
}: {
  table: Table<TData>;
  row: Row<TData>;
  cells: Cell<TData, unknown>[];
  isSelected: boolean;
  isPosition: boolean;
  isPinned?: boolean;
  allSelected: boolean;
  currentRef?: React.MutableRefObject<HTMLDivElement | null>;
} & React.HTMLAttributes<HTMLTableRowElement>) => {
  const chain =
    <T,>(cb?: (ev: T) => any) =>
    (ev: T) => {
      if (
        row.isPreviewEnabled() &&
        (!row.getIsPosition() || (table.getState().preview ?? 0) === 0) &&
        row
          .getVisibleCells()
          .findIndex((cell) => !cell.column.isPreviewEnabled()) < 0 // no disabled cells
      ) {
        row.setIsPosition(true);
        table.setPreview((u) =>
          u === 0 ? (table.isSkipPreview() ? 2 : 1) : u,
        );
        return;
      }
      if (cb) {
        cb(ev);
      }
    };
  return (
    <TableRow__
      data-component="data-table-row"
      data-is-selected={isSelected}
      data-is-position={isPosition}
      data-is-pinned={isPinned}
      {...props}
      key={row.id}
      onClick={chain(onClick)}
      className={cn(
        row.getIsPosition() && "bg-selected/80",
        isPinned && "bg-lavender/20 hover:bg-lavender/30",
        className,
      )}
      ref={(el) => {
        if (row.getIsPosition()) {
          if (currentRef) {
            currentRef.current = el;
          }
          el?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }}
    >
      {row.getVisibleCells().map((cell) => {
        const meta = (cell?.column?.columnDef?.meta as any) ?? {};
        const chain =
          <T,>(cb?: (ev: T) => any) =>
          (ev: T) => {
            if (
              cell.column.isPreviewEnabled() &&
              (!row.getIsPosition() || (table.getState().preview ?? 0) === 0)
            ) {
              row.setIsPosition(true);
              table.setPreview((u) =>
                u === 0 ? (table.isSkipPreview() ? 2 : 1) : u,
              );
              return;
            }
            if (cb) {
              cb(ev);
            }
          };
        return (
          <TableCell_
            {...meta?.props}
            key={cell.id}
            className={cn(cell.column.isPreviewEnabled() && "cursor-pointer")}
            style={{
              ...meta?.props?.style,
              width: renderedColumnWidth(
                meta?.width,
                `calc(var(--col-${cell.column.id}-size))`,
              ),
              minWidth: meta?.minWidth,
              maxWidth: meta?.maxWidth,
            }}
            onClick={chain(meta?.props?.onClick)}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell_>
        );
      })}
    </TableRow__>
  );
};

const TableRow = React.memo(
  TableRow_,
  (a, b) =>
    a.row.id === b.row.id &&
    a.row.original === b.row.original &&
    a.cells === b.cells &&
    a.isSelected === b.isSelected &&
    a.isPosition === b.isPosition &&
    a.allSelected === b.allSelected &&
    a.isPinned === b.isPinned &&
    a.className === b.className,
) as typeof TableRow_;

export default function DataTableColumns<TData, TValue = any>({
  table,
  columns,
  fill = true,
  loading,
  scroll,
  currentRef,
  noResultsMessage = "No results found.",
  accordionGrouping,
  onToggleAccordionGroup,
  stickyHeader,
  stickyFooter,
  footerAsSecondaryHeader,
  ...props
}: DataTableColumnsProps<TData, TValue>) {
  /**
   * Instead of calling `column.getSize()` on every render for every header
   * and especially every data cell (very expensive),
   * we will calculate all column sizes at once at the root table level in a useMemo
   * and pass the column sizes down as CSS variables to the <table> element.
   */
  const emptyMessage = useNoResultsMessage(noResultsMessage);
  const headerRef = React.useRef<HTMLTableSectionElement | null>(null);
  const [headerHeight, setHeaderHeight] = React.useState(0);
  React.useLayoutEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.getBoundingClientRect().height);
    }
  }, [loading]);

  /**
   * Width available to the columns, tracked so `fill` sizing can resolve to
   * absolute pixels (see `columnSizeVars`). Measured on the wrapper rather than
   * the <table>, whose own width is what we are computing.
   *
   * Both readings are layout-based on purpose. `getBoundingClientRect()` would
   * report the CSS-*transformed* visual box, so a table mounted inside an
   * animating dialog (`zoom-in-95`) would measure ~95% of its real width — and
   * because dropping a transform never changes the layout box, no resize
   * notification would ever arrive to correct it.
   */
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [availableWidth, setAvailableWidth] = React.useState(0);
  const measureContainer = React.useCallback(() => {
    if (containerRef.current) {
      setAvailableWidth(containerRef.current.clientWidth);
    }
  }, []);
  React.useLayoutEffect(measureContainer, [measureContainer]);
  // Re-read the element rather than trusting the notification's own
  // `contentRect`: the shared observer replays only the first entry per target
  // per frame, so a coalesced batch would otherwise leave this permanently
  // stale. Reading `clientWidth` in both paths also keeps a single source, so
  // mount does not measure fractionally here and integrally there.
  useResizeObserver(containerRef, measureContainer);

  const [showLoader, setShowLoader] = React.useState(false);
  React.useEffect(() => {
    if (!loading) {
      setShowLoader(false);
      return;
    }
    const timer = setTimeout(() => setShowLoader(true), 500);
    return () => clearTimeout(timer);
  }, [loading]);

  const columnSizeVars = React.useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes: { [key: string]: string } = {};
    if (!fill) {
      for (let i = 0; i < headers.length; i++) {
        const header = headers[i]!;
        colSizes[`--header-${header.id}-size`] = `${header.getSize()}px`;
        colSizes[`--col-${header.column.id}-size`] =
          `${header.column.getSize()}px`;
      }
      return colSizes;
    }

    const fixedPxByHeaderId = new Map<string, number>();
    let fixedPxTotal = 0;
    let flexibleTotal = 0;

    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!;
      const fixedPx = parsePixelWidth(header.column.columnDef.meta?.width);
      if (fixedPx !== undefined) {
        fixedPxByHeaderId.set(header.id, fixedPx);
        fixedPxTotal += fixedPx;
      } else {
        flexibleTotal += header.getSize();
      }
    }

    /**
     * Sticky tables render with `table-layout: fixed`, which silently discards a
     * column width that mixes a percentage with a length — `calc(20% - 6.4px)`
     * resolves to `auto`, so the browser splits the space equally and dragging a
     * resize handle has no visible effect. Resolving the leftover space to
     * absolute pixels keeps the width to a single unit the fixed-layout
     * algorithm can honor, in both `fixed` and `auto` layout.
     */
    const flexiblePx = availableWidth - fixedPxTotal;
    // Require real leftover room, not merely a non-zero measurement: while a
    // resizable panel animates shut the container can get narrower than the
    // fixed columns alone, and emitting `0px` would collapse every other column.
    const measured = flexiblePx > 0;

    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!;
      const fixedPx = fixedPxByHeaderId.get(header.id);

      if (fixedPx !== undefined) {
        colSizes[`--header-${header.id}-size`] = `${fixedPx}px`;
        colSizes[`--col-${header.column.id}-size`] = `${fixedPx}px`;
        continue;
      }

      const ratio = header.getSize() / (flexibleTotal || 1);
      const width = measured
        ? `${flexiblePx * ratio}px`
        : // Until the first measurement lands, approximate with percentages.
          `calc(${ratio * 100}% - ${fixedPxTotal * ratio}px)`;
      colSizes[`--header-${header.id}-size`] = width;
      colSizes[`--col-${header.column.id}-size`] = width;
    }
    return colSizes;
  }, [
    table.getState().columnSizingInfo,
    table.getState().columnSizing,
    // Widths are authoritative pixels now, so a column that becomes visible
    // after the last recompute would otherwise have no variable at all — and
    // resolve to `auto`, i.e. zero width, with no space left to claim.
    table.getState().columnVisibility,
    columns,
    fill,
    availableWidth,
  ]);

  /**
   * `AccordionTableRows` renders one <col> per entry in its own nested
   * `table-layout: fixed` table. A hidden column has no `--col-*-size`
   * variable, so its <col> would resolve to `auto` — zero width, since the
   * visible columns' pixels already fill the table — silently shifting every
   * later cell under the wrong header. Feed it only what the header row renders.
   */
  const visibleColumns = React.useMemo(() => {
    const visibleIds = new Set(table.getVisibleLeafColumns().map((c) => c.id));
    return columns.filter((column) => {
      // Mirror TanStack's own id derivation (getColumnDefId), or a column whose
      // id we failed to reconstruct would be dropped from the group even while
      // visible. Anything we still cannot identify is kept: over-rendering a
      // <col> is recoverable, silently losing a real column is not.
      const id = getColumnDefId(column);
      return id === undefined || visibleIds.has(id);
    });
  }, [columns, table.getState().columnVisibility]);

  const footer = React.useMemo(() => {
    const footers = table
      .getFooterGroups()
      .filter((footerGroup) =>
        footerGroup.headers.find((header) => header.column.columnDef.footer),
      );
    if (footers.length <= 0) return undefined;
    const Wrap = footerAsSecondaryHeader ? TableHeader : TableFooter;
    return (
      <>
        <Wrap
          data-component="data-table-footers"
          className={cn(
            footerAsSecondaryHeader && "bg-muted",
            stickyFooter && "sticky bg-muted z-50 border-b",
            stickyFooter && !footerAsSecondaryHeader && "bottom-0",
            stickyFooter && footerAsSecondaryHeader && "top-10",
          )}
        >
          {table.getFooterGroups().map((footerGroup) => (
            <TableRow__ key={footerGroup.id}>
              {footerGroup.headers.map((header) => {
                return (
                  <TableCell_
                    key={header.id}
                    colSpan={header.colSpan}
                    className={cn(
                      "relative first:rounded-tl-md first:rounded-bl-md last:rounded-tr-md last:rounded-br-md",
                    )}
                    style={{
                      width: renderedColumnWidth(
                        header.column.columnDef.meta?.width,
                        `calc(var(--header-${header?.id}-size))`,
                      ),
                      minWidth: header.column.columnDef.meta?.minWidth,
                      maxWidth: header.column.columnDef.meta?.maxWidth,
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.footer,
                          header.getContext(),
                        )}
                  </TableCell_>
                );
              })}
            </TableRow__>
          ))}
        </Wrap>
      </>
    );
  }, [
    columns,
    table.getState().columnVisibility,
    table.getState().rowSelection,
    footerAsSecondaryHeader,
  ]);

  const inner = (
    <>
      {!!showLoader && (
        <div
          className="absolute left-0 right-0 bottom-0 z-[60] flex items-center justify-center pointer-events-none"
          style={{ top: headerHeight }}
        >
          <Loader data-component="data-table-loading" size="lg" />
        </div>
      )}
      <div className="relative" ref={containerRef}>
        <Table_
          {...props}
          className={cn(fill && "w-full", props.className)}
          container={
            stickyHeader || stickyFooter
              ? {
                  className: cn("relative w-full overflow-visible"),
                }
              : {
                  className: cn("relative w-full overflow-auto"),
                }
          }
          style={{
            ...props.style,
            ...columnSizeVars, // Define column sizes on the <table> element
            ...((stickyHeader || stickyFooter) && {
              tableLayout: "fixed",
              borderCollapse: "separate",
              borderSpacing: 0,
            }),
          }}
        >
          <TableHeader
            ref={headerRef}
            data-component="data-table-headers"
            className={cn(
              stickyHeader && "sticky top-0 z-50 bg-accent border-b",
            )}
          >
            {React.useMemo(
              () =>
                table.getHeaderGroups().map((headerGroup) => (
                  <TableRow__ key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead
                          key={header.id}
                          colSpan={header.colSpan}
                          className={cn(
                            "relative first:rounded-tl-md first:rounded-bl-md last:rounded-tr-md last:rounded-br-md",
                            stickyHeader && "bg-accent",
                          )}
                          style={{
                            width: renderedColumnWidth(
                              header.column.columnDef.meta?.width,
                              `calc(var(--header-${header?.id}-size))`,
                            ),
                            minWidth: header.column.columnDef.meta?.minWidth,
                            maxWidth: header.column.columnDef.meta?.maxWidth,
                          }}
                        >
                          {header.isPlaceholder ? null : (
                            <DataTableColumnHeader
                              column={header.column}
                              title={flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            />
                          )}
                          {header.column.getCanResize() &&
                            parsePixelWidth(
                              header.column.columnDef.meta?.width,
                            ) === undefined && (
                              <div
                                className="absolute select-none touch-none top-0 right-0 cursor-col-resize w-1 h-full after:content-[' '] after:absolute after:h-full after:bg-border/40 after:hover:bg-border/90 after:active:bg-border after:w-0.5 after:active:w-1 after:hover:w-1"
                                onDoubleClick={() => header.column.resetSize()}
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                              />
                            )}
                        </TableHead>
                      );
                    })}
                  </TableRow__>
                )),
              [
                columns,
                table.getState().columnVisibility,
                table.getState().rowSelection,
              ],
            )}
          </TableHeader>
          {footerAsSecondaryHeader && footer}
          <TableBody
            data-component="data-table-rows"
            className={cn(showLoader && "blur-[1px] grayscale")}
          >
            {table.getRowModel().rows?.length
              ? accordionGrouping?.groupByColumn && onToggleAccordionGroup
                ? (() => {
                    const groups = groupRowsByColumn(
                      table.getRowModel().rows,
                      accordionGrouping.groupByColumn,
                    );
                    return (
                      <AccordionTableRows
                        groups={groups}
                        expandedGroups={accordionGrouping.expandedGroups}
                        onToggleGroup={onToggleAccordionGroup}
                        columns={visibleColumns}
                        groupByColumn={accordionGrouping.groupByColumn}
                        columnSizeVars={columnSizeVars}
                        renderRow={(row) => (
                          <React.Fragment key={row.id}>
                            <TableRow
                              table={table}
                              row={row}
                              cells={row.getVisibleCells()}
                              isSelected={row.getIsSelected()}
                              isPosition={row.getIsPosition()}
                              allSelected={table.getAllSelected()}
                              isPinned={
                                (row.original as TPinnedRowMeta)?.__meta?.pinned
                              }
                              className={row.getClassName()}
                              currentRef={currentRef}
                            />
                            {/* Opt-in, see data-table-row-detail.tsx */}
                            {table.options.renderRowDetail?.(row)}
                          </React.Fragment>
                        )}
                      />
                    );
                  })()
                : table.getRowModel().rows.map((row) => (
                    <React.Fragment key={row.id}>
                      <TableRow
                        table={table}
                        row={row}
                        cells={row.getVisibleCells()}
                        isSelected={row.getIsSelected()}
                        isPosition={row.getIsPosition()}
                        allSelected={table.getAllSelected()}
                        isPinned={
                          (row.original as TPinnedRowMeta)?.__meta?.pinned
                        }
                        className={row.getClassName()}
                        currentRef={currentRef}
                      />
                      {/* Opt-in, see data-table-row-detail.tsx */}
                      {table.options.renderRowDetail?.(row)}
                    </React.Fragment>
                  ))
              : !loading && (
                  <TableRow__ data-component="data-table-no-results">
                    <TableCell_
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      {emptyMessage}
                    </TableCell_>
                  </TableRow__>
                )}
          </TableBody>
          {!footerAsSecondaryHeader && footer}
        </Table_>
      </div>
    </>
  );

  if (!scroll) return inner;
  const {
    viewport: _vp,
    className: scrollClassName,
    style: scrollStyle,
    ...scrollRest
  } = scroll as any;
  return (
    <div
      {...scrollRest}
      className={cn("h-full overflow-auto", scrollClassName, _vp?.className)}
      style={{ ...scrollStyle, ..._vp?.style, position: "relative", zIndex: 1 }}
    >
      {inner}
    </div>
  );
}
